import { useEffect, useRef, useState } from 'react';
import { trackAdImpression } from '../utils/adTesting';

/**
 * Custom hook to monitor ad viewability
 * Tracks when ad is visible in viewport and for how long
 * 
 * @param {string} variant - A/B test variant
 * @param {string} position - Ad position identifier
 * @param {number} threshold - Percentage of ad that must be visible (0-1)
 * @returns {object} { ref, isViewable, viewableTime, impressionTracked }
 */
export function useAdViewability(variant, position, threshold = 0.5) {
  const ref = useRef(null);
  const [isViewable, setIsViewable] = useState(false);
  const [viewableTime, setViewableTime] = useState(0);
  const [impressionTracked, setImpressionTracked] = useState(false);
  const viewableStartTime = useRef(null);
  const viewableInterval = useRef(null);

  useEffect(() => {
    const element = ref?.current;
    if (!element) return;

    // Intersection Observer options
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: threshold // Ad must be 50% visible by default
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const isCurrentlyViewable = entry.isIntersecting && entry.intersectionRatio >= threshold;
        
        setIsViewable(isCurrentlyViewable);

        if (isCurrentlyViewable) {
          // Ad became viewable
          if (!viewableStartTime.current) {
            viewableStartTime.current = Date.now();
            
            // Track impression after 1 second of viewability (IAB standard)
            setTimeout(() => {
              if (viewableStartTime.current && !impressionTracked) {
                trackAdImpression(variant, position, true);
                setImpressionTracked(true);
              }
            }, 1000);
          }

          // Update viewable time every 100ms
          if (!viewableInterval.current) {
            viewableInterval.current = setInterval(() => {
              if (viewableStartTime.current) {
                const elapsed = Date.now() - viewableStartTime.current;
                setViewableTime(elapsed);
              }
            }, 100);
          }
        } else {
          // Ad became not viewable
          if (viewableStartTime.current) {
            const totalViewableTime = Date.now() - viewableStartTime.current;
            setViewableTime(totalViewableTime);
            viewableStartTime.current = null;
          }

          if (viewableInterval.current) {
            clearInterval(viewableInterval.current);
            viewableInterval.current = null;
          }
        }
      });
    }, options);

    observer?.observe(element);

    // Cleanup
    return () => {
      if (element) {
        observer?.unobserve(element);
      }
      if (viewableInterval?.current) {
        clearInterval(viewableInterval?.current);
      }
    };
  }, [variant, position, threshold, impressionTracked]);

  return {
    ref,
    isViewable,
    viewableTime,
    impressionTracked
  };
}